// Mud weight & the pressure window (M2_SPEC §2, DESIGN §5.3) for rotary wells.
//   MW      = well.mud.ppg, clamped to the mud's [ppgMin, ppgMax]
//   ECD     = MW + MUD.ecdPerKft × depth/1000 (circulating)
//   MWeff   = MW − swab (tripping) − 0.5 (lost circulation today)
//   window  = [max pore of open permeable zones, min frac of open strata] below the PRESSURE shoe
//             (the deepest cemented casing: uncemented casing stops caving and water, not pressure).
// Pure helpers + the daily kick / loss / differential-sticking rolls. All randomness via core/rng.js.
import { chance } from '../core/rng.js';
import { MUD } from '../data/m2/rules.js';
import { eqDef, fieldDef, stat, eraDollars, unitPrice, defsByCat, isAvailable } from './defs.js';
import { strataAt, reservoirsAt } from './geo.js';
import { clamp } from './util.js';
import { shaleMpdGain } from './m4/shale.js'; // M4-A: MPD on lateral wells

export const FRAC_CAP = 19;          // M2_SPEC §18.5: the field builder's frac grows without bound
export const DEFAULT_PPG = 9.0;      // a driller's habit with native clay
const PERMEABLE = new Set(['sand', 'sandstone', 'limestone', 'dolomite', 'gravel', 'conglomerate', 'chalk', 'reef', 'coal']);

export const isPermeable = (s) => !!(s.water || s.gas || s.lossZone || PERMEABLE.has(s.rock));

export function mudDef(well) {
  return well?.mud?.defId ? eqDef(well.mud.defId) : null;
}

export function mudRange(def) {
  return [stat(def, 'ppgMin', 8.3), stat(def, 'ppgMax', 8.8)];
}

export function mudWeight(well) {
  const def = mudDef(well);
  const ppg = Number(well?.mud?.ppg) || DEFAULT_PPG;
  if (!def) return ppg;
  const [lo, hi] = mudRange(def);
  return clamp(ppg, lo, hi);
}

export const cakeQuality = (def) => stat(def, 'cakeQuality', 0);

// Rotary mud available now (historical year reached, researched, or — before 1901 — because the player owns a
// researched rotary rig: M2_SPEC §18.6, a rotary rig's consumables come with it).
export function mudAvailable(state, def) {
  if (!def || def.cat !== 'mud') return false;
  if (isAvailable(def, state)) return true;
  return def.year <= 1902 && Object.values(state.rigs ?? {}).some((r) => stat(eqDef(r.defId), 'rigType', 'cable') === 'rotary');
}

// Cheapest mud that can hold `ppg` (null: none can). Prefers what the well already runs.
export function bestMudFor(state, ppg, current = null) {
  const cur = current ? eqDef(current) : null;
  if (cur && mudAvailable(state, cur) && ppg <= mudRange(cur)[1] + 1e-9 && ppg >= mudRange(cur)[0] - 1e-9) return cur;
  const list = defsByCat('mud').filter((d) => mudAvailable(state, d) && mudRange(d)[1] >= ppg - 1e-9 && mudRange(d)[0] <= Math.max(ppg, 8.8) + 1e-9);
  list.sort((a, b) => stat(a, 'pricePerFt', 1) - stat(b, 'pricePerFt', 1));
  return list[0] ?? null;
}

// Default mud for a new rotary well: native clay (cake, 8.8–10) when known, else water.
export function defaultMud(state) {
  const list = defsByCat('mud').filter((d) => mudAvailable(state, d) && d.year < 1950);
  const pick = list.find((d) => d.id === 'native_clay') ?? list.sort((a, b) => cakeQuality(b) - cakeQuality(a))[0] ?? null;
  return pick;
}

// Depth of the pressure seal: deepest casing string whose cement has set.
export function pressureShoe(well) {
  let d = 0;
  for (const c of well.casing ?? []) if (c.cemented) d = Math.max(d, c.bottom);
  return d;
}

function shoeBonus(well) {
  let best = null;
  for (const c of well.casing ?? []) if (c.cemented && (!best || c.bottom > best.bottom)) best = c;
  return best ? stat(eqDef(best.cemented), 'shoeFracBonus', 0.7) : 0;
}

// Effective pore pressure of a reservoir: overpressure bleeds off as the pool is drained (rule of capture:
// by 1903 Spindletop's cap rock no longer gushes).
export function reservoirPore(r, rs) {
  const pr = rs ? clamp(rs.pressure ?? 1, 0, 1) : 1;
  return 8.4 + ((r.pore ?? 9) - 8.4) * pr;
}

// Every zone the hole has penetrated, with open/cased flags (for the gauge) — strata and reservoirs.
export function zoneProfile(state, well) {
  const fdef = fieldDef(well.fieldId);
  const field = state.fields[well.fieldId];
  const plot = field.plots[well.plot];
  const strata = strataAt(fdef, plot);
  const pShoe = pressureShoe(well);
  const depth = well.depth;
  const out = [];
  for (const s of strata) {
    if (s.top >= depth - 1e-6) break;
    out.push({
      kind: s.water ? 'water' : s.gas ? 'gas' : 'formation', key: `s${s.idx}`, idx: s.idx, name: s.name ?? s.rock, rock: s.rock,
      top: s.top, bottom: Math.min(s.bottom, depth), fullBottom: s.bottom,
      pore: s.pore ?? 8.6, frac: Math.min(FRAC_CAP, s.frac ?? 14), permeable: isPermeable(s), gas: !!s.gas, water: !!s.water,
      lossZone: !!s.lossZone, salt: !!s.salt, heaving: !!s.heaving, h2s: s.h2s ?? 0,
      open: s.bottom > pShoe + 1e-6, cased: (well.shoe ?? 0) >= s.bottom - 1e-6, cemented: pShoe >= s.bottom - 1e-6,
    });
  }
  for (const r of reservoirsAt(fdef, plot)) {
    if (r.top >= depth - 1e-6) continue;
    const bottom = Math.min(r.top + r.thick, depth);
    out.push({
      kind: 'reservoir', key: `r:${r.id}`, resId: r.id, name: r.name ?? r.id, rock: 'pay', top: r.top, bottom, fullBottom: r.top + r.thick,
      pore: reservoirPore(r, field.reservoirs[r.id]), frac: FRAC_CAP, permeable: true, gas: !!r.gasCap, water: false,
      lossZone: false, salt: false, heaving: false, h2s: r.h2s ?? 0,
      open: r.top + r.thick > pShoe + 1e-6, cased: (well.shoe ?? 0) >= r.top + r.thick - 1e-6, cemented: pShoe >= r.top + r.thick - 1e-6,
    });
  }
  return out;
}

// The pressure window over the open hole. opts: { tripping, lost, drilling }
export function pressureWindow(state, well, opts = {}) {
  const zones = zoneProfile(state, well);
  const pShoe = pressureShoe(well);
  const bonus = shoeBonus(well);
  let pore = 8.4, frac = FRAC_CAP;
  for (const z of zones) {
    if (!z.open) continue;
    if (z.permeable && z.kind !== 'water') pore = Math.max(pore, z.pore);
    if (z.kind !== 'reservoir') {
      const atShoe = pShoe > 0 && z.top <= pShoe + 1e-6;
      frac = Math.min(frac, z.frac + (atShoe ? bonus : 0));
    }
  }
  const mw = mudWeight(well);
  const ecd = mw + MUD.ecdPerKft * (well.depth / 1000);
  const swab = opts.tripping ? MUD.swabPpg : 0;
  let mwEff = mw - swab - (opts.lost ? 0.5 : 0);
  if (well.lateral) { const g = shaleMpdGain(state, well) / 2; frac += g; mwEff += g; } // M4-A: MPD widens the usable window (≥ 2000 laterals only)
  return {
    pore, frac, mw, mwEff, ecd, swab, tripping: !!opts.tripping,
    marginLow: mwEff - pore, marginHigh: frac - (opts.drilling === false ? mw : ecd),
    pressureShoe: pShoe, zones,
  };
}

// Barite surcharge per foot drilled above MUD.bariteAboveppg (era $).
export function baritePerFt(state, ppg) {
  if (ppg <= MUD.bariteAboveppg + 1e-9) return 0;
  return eraDollars(state, MUD.bariteCostPerTenthPpgFt * (ppg - MUD.bariteAboveppg) / 0.1, MUD.baritePriceYear);
}

// Mud cost per foot (era $), before stock and the mud man's discount.
export function mudPerFt(state, def) {
  if (!def) return 0;
  return unitPrice(state, def);
}

// Daily open-hole pressure rolls. ctx: { tripping, drilling, ftToday, mudMan }
// Returns { kick: zone|null, loss: bool, stuck: 'diff'|'loss'|null, window, waterFlow: zone|null }.
export function pressureRolls(state, well, ctx = {}) {
  const w0 = pressureWindow(state, well, { tripping: ctx.tripping, drilling: ctx.drilling });
  const def = mudDef(well);
  const cake = cakeQuality(def);
  const out = { kick: null, loss: false, stuck: null, window: w0, waterFlow: null };
  if (state._rush) return out;
  // lost circulation: circulating ECD (static MW when not drilling) above the weakest open stratum
  const load = ctx.drilling ? w0.ecd : w0.mw;
  if (load > w0.frac + 1e-9) {
    out.loss = true;
    if (chance(state, MUD.lostCircStuckPerDay * stat(def, 'stuckMult', 1))) out.stuck = 'loss';
  }
  const mwEff = w0.mwEff - (out.loss ? 0.5 : 0);
  const fresh = Math.max(0, well.depth - (ctx.ftToday ?? 0));
  for (const z of w0.zones) {
    if (!z.open || !z.permeable) continue;
    if (z.kind === 'water') { if (z.pore > mwEff + 1e-9) out.waterFlow ??= z; continue; }
    if (z.pore <= mwEff + 1e-9) continue;
    // grace day: the bit only just opened this zone — the gas-cut-mud show (rotary.js showNotice) pauses the
    // game so the player can weight up before the zone can kick
    if (z.idx != null && well.passed?.[z.idx] === state.day) continue;
    let p = Math.min(0.9, MUD.kickPerPpg * (z.pore - mwEff)) * (z.gas ? MUD.gasKickMult : 1);
    if (ctx.drilling && z.bottom > fresh + 1e-6) p *= 1.5;      // fresh rock exposed today
    if (ctx.mudMan) p *= 0.7;                                    // catches the pit gain early
    if (chance(state, Math.min(0.95, p))) { out.kick = z; break; }
  }
  // differential sticking across permeable sands (heavy overbalance, thin cake)
  if (!out.stuck && !ctx.tripping) {
    let over = 0;
    for (const z of w0.zones) if (z.open && z.permeable) over = Math.max(over, w0.mw - z.pore - MUD.diffStickFreePpg);
    if (over > 0) {
      const p = MUD.diffStickPerPpgDay * over * (1 - cake) * stat(def, 'stuckMult', 1);
      if (chance(state, p)) out.stuck = 'diff';
    }
  }
  return out;
}

// Pore the crew knows for a zone: kicks, shows, production tests (zonePore maps on the well and the field — offset
// wells) and e-logs (field.poreLog, written by sim/m2/geophysics.js). null when unknown.
// well.knownPore stays a single number (max ppg known on the well) for the UI / CONTRACT.
export function poreKnown(state, well, z) {
  const f = state.fields[well.fieldId];
  const k = well.zonePore?.[z.key] ?? f?.zonePore?.[z.key];
  if (k != null) return k;
  if (f?.poreLog?.length && z.top != null) {
    let best = null;
    for (const e of f.poreLog) {
      if (z.resId ? e.resId === z.resId : (e.top < (z.bottom ?? z.top + 1) && e.bottom > z.top && !e.resId)) best = Math.max(best ?? 0, e.pore);
    }
    return best;
  }
  return null;
}

export function learnPore(state, well, z, ppg = z.pore) {
  const v = Math.round(ppg * 10) / 10;
  (well.zonePore ??= {})[z.key] = v;
  const f = state.fields[well.fieldId];
  if (f) (f.zonePore ??= {})[z.key] = v;
  well.knownPore = Math.max(typeof well.knownPore === 'number' ? well.knownPore : 0, v);
}

// Mud man / toolpusher auto weight: max(known or estimated pore of open + upcoming zones) + 0.3, kept under the frac
// limit when the window allows. Known pore (kicks, e-logs, offset wells, production tests) is exact; otherwise the
// toolpusher's estimate is off by well.poreGuess (±0.5, fixed per well) — a mud man halves that error.
export function autoMwTarget(state, well, { withMudMan = false, lookAheadFt = 300 } = {}) {
  const fdef = fieldDef(well.fieldId);
  const field = state.fields[well.fieldId];
  const plot = field.plots[well.plot];
  const pShoe = pressureShoe(well);
  const guessErr = (well.poreGuess ?? 0) * (withMudMan ? 0.5 : 1);
  let need = 8.6;
  const consider = (z) => {
    const k = poreKnown(state, well, z);
    need = Math.max(need, k ?? z.pore + guessErr);
  };
  const horizon = well.depth + lookAheadFt;
  for (const s of strataAt(fdef, plot)) {
    if (s.top >= horizon) break;
    if (s.bottom <= pShoe || !isPermeable(s) || s.water) continue;
    consider({ key: `s${s.idx}`, pore: s.pore ?? 8.6, top: s.top, bottom: s.bottom });
  }
  // Reservoirs are only weighted for once someone knows they are there (a kick, a strike, an offset well):
  // the first well into a new pool finds out the hard way.
  for (const r of reservoirsAt(fdef, plot)) {
    if (r.top >= horizon || r.top + r.thick <= pShoe) continue;
    const k = poreKnown(state, well, { key: `r:${r.id}`, resId: r.id, top: r.top, bottom: r.top + r.thick });
    if (k != null) need = Math.max(need, Math.min(k, reservoirPore(r, field.reservoirs[r.id])));
  }
  let target = need + MUD.killMarginPpg;
  const w = pressureWindow(state, well, { drilling: true });
  const ecdExtra = w.ecd - w.mw;
  const ceiling = w.frac - ecdExtra - 0.05;
  if (target > ceiling) target = Math.max(need + 0.05, ceiling);
  return Math.round(target * 10) / 10;
}
